import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';

// Resolve the signed-in creator's world. Since 01.11.2026 the cabinet opens right after the
// @handle is entered: `provider` is the verified creator OR the one behind the open claim
// (creator_me → my_creator_view_pid), and `verified` says which. Read-only screens work
// either way; acting as the creator (apply, collab, edit profile) needs verified.

export interface CreatorProvider { id: string; handle: string | null; profile_pic_mirrored_url: string | null; niche_ids: string[] | null; verified: boolean }
export interface CreatorSnapshot {
  followers: number | null;
  median_reel_views: number | null;
  engagement_rate: number | null;
  suggested_reel_price: number | null;
  calculated_reel_price: number | null;
}
export interface PendingClaim {
  id: string; claim_code: string; instagram_provider_id: string; status: string; handle?: string | null;
}

export interface CreatorMe {
  provider: CreatorProvider | null;   // verified creator, or the one behind the open claim
  verified: boolean;                  // false → read-only cabinet + «подтвердите аккаунт»
  snapshot: CreatorSnapshot | null;
  pendingClaim: PendingClaim | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

export function useCreator(): CreatorMe {
  const { user } = useAuth();

  const provQ = useSupabaseQuery<CreatorProvider | null>(
    ['creator-me', user?.id],
    async () => {
      if (!user) return { data: null, error: null };
      // SECURITY DEFINER: a pending claimant can't read the row through RLS yet.
      return supabase.rpc('creator_me');
    },
    // This read gates the whole app (claim vs. cabinet). On flaky links it can
    // error even though the server answered 200; refetch when the tab regains
    // focus so returning to the app self-heals instead of stranding the user
    // on the error/claim screen (the global default has focus-refetch off).
    // Polling while unverified: the background verifier may confirm the claim.
    { enabled: !!user, refetchOnWindowFocus: true, refetchInterval: (q) => (q.state.data && !(q.state.data as CreatorProvider).verified ? 30_000 : false) },
  );
  const provider = provQ.data ?? null;
  const verified = !!provider?.verified;

  const snapQ = useSupabaseQuery<CreatorSnapshot | null>(
    ['creator-snapshot', provider?.id],
    async () => {
      if (!provider) return { data: null, error: null };
      return supabase
        .from('ig_profile_snapshots')
        .select('followers, median_reel_views, engagement_rate, suggested_reel_price, calculated_reel_price')
        .eq('instagram_provider_id', provider.id)
        .order('scraped_date', { ascending: false })
        .limit(1)
        .maybeSingle();
    },
    { enabled: !!provider },
  );

  // pending claim only matters before verification (RLS: own rows) — its code is shown
  // in the «подтвердите аккаунт» card inside the cabinet.
  const claimQ = useSupabaseQuery<PendingClaim | null>(
    ['creator-pending-claim', user?.id],
    async () => {
      if (!user || verified) return { data: null, error: null };
      return supabase
        .from('creator_claims')
        .select('id, claim_code, instagram_provider_id, status, instagram_providers ( handle )')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    },
    { enabled: !!user && !verified },
  );
  const rawClaim = claimQ.data as (PendingClaim & { instagram_providers?: { handle: string } | null }) | null;
  const pendingClaim: PendingClaim | null = rawClaim
    ? { ...rawClaim, handle: rawClaim.instagram_providers?.handle ?? null }
    : null;

  return {
    provider,
    verified,
    snapshot: snapQ.data ?? null,
    pendingClaim,
    isLoading: provQ.isLoading || (!!provider && snapQ.isLoading) || (!verified && claimQ.isLoading),
    error: provQ.error ?? null,
    refetch: () => { provQ.refetch(); snapQ.refetch(); claimQ.refetch(); },
  };
}
